import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import {
  AllowStringForNumbers,
  EnergyMatchConfig,
  getPpas,
  ppaErrorString,
  ppaUpdate,
  RecursivePartialSiteAllowStringValues,
  Site,
  SiteAllowStringValues,
  SiteCalculations,
  SiteValuesOptions,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  SiteCalculationsProvider,
  useContextTS,
} from "@inrange/shared-components";
import { formatDateTimeFromSeconds } from "@inrange/theme-components";
import {
  formatBatteryModel,
  formatBatterySummary,
  formatCurrency,
  formatPercentage,
  formatTariff,
  formatUnits,
  fractionalCurrencySymbol,
} from "@inrange/theme-components/formatting";
import { sortBy } from "@inrange/utils";
import deepmerge from "deepmerge";
import React, { useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { STATUS_OPTIONS } from "./site/operationalStatusUtils";
import { overwriteMerge } from "./site/site-utils";
import { getCurtailmentDetails } from "./site/siteSections/dno-utils";
import { SITE_ORG_LABELS } from "./site/siteSections/enums";
import stringToReactNodesWithLinks from "./site/siteSections/stringToReactNodesWithLinks";
import {
  formatNumber,
  inverterRatedOutputPowerTotalKwpAc,
  parseAsNumber,
} from "./site/siteSections/utils";
import {
  getOrgSpv,
  getOwnership,
  getSiteOwnership,
  siteIRR,
} from "./sites-table-utils";

const validIRR = (
  site: Site,
  siteCalculations: SiteCalculations,
  organisation: Organisation
) => {
  if (
    organisation.orgType === "Tenant" ||
    !organisation.minIRR ||
    site.totalInstalledCapacity === 0
  ) {
    return true;
  }
  const ownership = getOwnership(site, organisation);
  if (ownership !== "landlord" && ownership !== "ownerOccupier") {
    return true;
  }
  const irr = siteCalculations.financialModels![ownership!]["license"].irr;
  return irr !== undefined && irr >= organisation.minIRR;
};

const validTenantTariff = (
  site: Site,
  organisation: Organisation,
  tariff: Partial<AllowStringForNumbers<EnergyMatchConfig>>
) => {
  if (organisation.orgType === "Landlord" || !organisation.maxTenantTariff) {
    return true;
  }
  const ownership = getOwnership(site, organisation);
  if (ownership !== "tenant" && ownership !== "ownerOccupier") {
    return true;
  }
  return parseAsNumber(tariff.tariff) <= organisation.maxTenantTariff;
};

const validNetworkImportTariff = (site: Site, organisation: Organisation) => {
  if (
    organisation.orgType === "Landlord" ||
    !organisation.maxNetworkImportTariff
  ) {
    return true;
  }
  const ownership = getOwnership(site, organisation);
  if (ownership !== "tenant" && ownership !== "ownerOccupier") {
    return true;
  }
  return (
    site.tariffConfig.networkImportTariff <= organisation.maxNetworkImportTariff
  );
};

const SitesTableRow = ({
  isFullSiteList,
  siteValuesOptions,
  index,
  organisation,
  site,
  thisSiteId,
  testMode,
  isSelected,
  setSelected,
  inlineEditMode,
  inlineEdits,
  setInlineEdits,
  setInlineSiteCalculations,
}: {
  isFullSiteList?: boolean;
  siteValuesOptions: SiteValuesOptions;
  index: number;
  organisation?: Organisation;
  site: Site;
  thisSiteId?: string;
  testMode: boolean;
  isSelected: boolean;
  setSelected: (siteId: string, selected: boolean) => void;
  inlineEditMode: boolean;
  inlineEdits: { site: RecursivePartialSiteAllowStringValues };
  setInlineEdits: (
    siteId: string,
    edits: {
      site: RecursivePartialSiteAllowStringValues;
    }
  ) => void;
  setInlineSiteCalculations: React.Dispatch<
    React.SetStateAction<{ [key: string]: SiteCalculations }>
  >;
}) => {
  const previewSite = useMemo(() => {
    if (!inlineEdits) return site;
    const result = deepmerge(site, inlineEdits?.site ?? {}, {
      arrayMerge: overwriteMerge,
    });
    // Make sure we don't accidentally mutate this value locally
    Object.freeze(result);
    Object.freeze(result.siteOwnerships);
    return result;
  }, [site, inlineEdits]);

  return (
    <SiteCalculationsProvider
      site={previewSite as unknown as Partial<Site>}
      originalSite={site}
      app="admin"
      isBulkEdit={true}
      disableUpdates={isFullSiteList || !inlineEditMode}
    >
      <SiteRowWithRecalculatingValues
        isFullSiteList={isFullSiteList}
        siteValuesOptions={siteValuesOptions}
        index={index}
        organisation={organisation}
        site={previewSite as unknown as SiteAllowStringValues}
        thisSiteId={thisSiteId}
        testMode={testMode}
        isSelected={isSelected}
        setSelected={setSelected}
        inlineEditMode={inlineEditMode}
        inlineEdits={inlineEdits}
        setInlineEdits={setInlineEdits}
        setInlineSiteCalculations={setInlineSiteCalculations}
      />
    </SiteCalculationsProvider>
  );
};

const MemoizedSitesTableRow = React.memo(SitesTableRow);

const SiteRowWithRecalculatingValues = ({
  isFullSiteList,
  siteValuesOptions,
  index,
  organisation,
  site,
  thisSiteId,
  testMode,
  isSelected,
  setSelected,
  inlineEditMode,
  inlineEdits,
  setInlineEdits,
  setInlineSiteCalculations,
}: {
  isFullSiteList?: boolean;
  siteValuesOptions: SiteValuesOptions;
  index: number;
  organisation?: Organisation;
  site: SiteAllowStringValues;
  thisSiteId?: string;
  testMode: boolean;
  isSelected: boolean;
  setSelected: (siteId: string, selected: boolean) => void;
  inlineEditMode: boolean;
  inlineEdits: { site: RecursivePartialSiteAllowStringValues };
  setInlineEdits: (
    siteId: string,
    edits: {
      site: RecursivePartialSiteAllowStringValues;
    }
  ) => void;
  setInlineSiteCalculations: React.Dispatch<
    React.SetStateAction<{ [key: string]: SiteCalculations }>
  >;
}) => {
  const siteRaw = site as unknown as Site;
  const { siteCalculations, isCalculating, errors } = useContextTS(
    SiteCalculationsContext
  );
  useEffect(() => {
    setInlineSiteCalculations((prev) => ({
      ...prev,
      [site.id]: siteCalculations,
    }));
  }, [setInlineSiteCalculations, site.id, siteCalculations]);
  const [curtailment, curtailmentRisk, curtailmentColor] =
    getCurtailmentDetails(site, siteCalculations);

  const distinctDemandCoefficients = isFullSiteList
    ? new Set<number>()
    : Object.values(site.demand)
        .filter((d) => !!d)
        .map((d) =>
          d.halfHourlyDemand?.source !== "inferred"
            ? // Return -1 for actual or generated data
              -1
            : // Return the defined demand coefficient for inferred data
              parseAsNumber(d.demandCoefficientKWhPerM2)
        )
        .reduce((acc, curr) => acc.add(curr), new Set<number>());
  const demandCoefficient =
    !isFullSiteList &&
    distinctDemandCoefficients.size === 1 &&
    distinctDemandCoefficients.values().next().value! !== -1
      ? distinctDemandCoefficients.values().next().value!
      : siteRaw.totalBuildingArea > 0
        ? siteCalculations.energyFlowAnnual!.demand /
          siteCalculations.totalBuildingArea
        : 0;

  const showCustomerIrr =
    site.activeInvestmentModel === "license" &&
    parseAsNumber(site.totalInstalledCapacity) > 0;
  const showInrangeIrr =
    site.activeInvestmentModel === "lease" &&
    parseAsNumber(site.totalInstalledCapacity) > 0;

  return (
    <tr>
      <td style={{ width: "1%" }}>
        {!site.locked && (
          <Form.Check
            type={"checkbox"}
            label={""}
            checked={isSelected}
            onChange={() => setSelected(site.id, !isSelected)}
            data-testid={`orgsites-select-${index}`}
          />
        )}
      </td>
      <td style={{ width: "28%" }}>
        <div style={{ marginBottom: "8px" }}>
          {inlineEditMode && (
            <Form.Control
              className="form-control-ghost w-100"
              as="textarea"
              rows={4}
              style={{ fontSize: 14 }}
              value={site.name}
              onChange={(e) =>
                setInlineEdits(site.id, {
                  site: {
                    name: e.target.value,
                  },
                })
              }
              data-testid={`inline-edit-site-name-${index}`}
            />
          )}
          {!inlineEditMode && (
            <div
              style={{ fontWeight: thisSiteId === site.id ? "bold" : "normal" }}
            >
              {site.locked ? <span title="Locked">🔒 </span> : ""}
              {thisSiteId === site.id && "Current site: "}
              <Link to={`/site/${site.id}/edit`}>{site.name}</Link>
            </div>
          )}
        </div>
        {site.sdmExcludeFromMatching ? (
          <div>(Excluded from customer-facing SDM matches to other sites)</div>
        ) : (
          ""
        )}
        {site.sdmHideMatches ? (
          <div>(No customer-facing SDM matches shown on this site)</div>
        ) : (
          ""
        )}
        <div style={{ fontSize: 10, color: "gray", marginBottom: "8px" }}>
          {testMode ? "fixed-id-for-testing" : site.id}
        </div>
        <div style={{ fontSize: 13 }}>
          {organisation && getSiteOwnership(siteRaw, organisation)?.park && (
            <div>Park: {getSiteOwnership(siteRaw, organisation)?.park}</div>
          )}
          {organisation &&
            getSiteOwnership(siteRaw, organisation)?.spv &&
            getOrgSpv(
              organisation,
              getSiteOwnership(siteRaw, organisation)!.spv!
            ) && (
              <div>
                SPV:{" "}
                {
                  getOrgSpv(
                    organisation,
                    getSiteOwnership(siteRaw, organisation)!.spv!
                  )!.name
                }
              </div>
            )}
          {(site.score || inlineEditMode) && (
            <div>
              Score:{" "}
              {inlineEditMode ? (
                <Form.Select
                  className="form-control-ghost d-inline-block"
                  style={{ width: "100px", fontSize: 13, padding: "4px 8px" }}
                  value={site.score || ""}
                  onChange={(e) =>
                    setInlineEdits(site.id, {
                      site: {
                        score:
                          "" === e.target.value
                            ? null
                            : parseFloat(e.target.value),
                      },
                    })
                  }
                  data-testid={`inline-edit-site-score-${index}`}
                >
                  {[
                    ["", "No score"],
                    [1, 1],
                    [2, 2],
                    [3, 3],
                    [4, 4],
                    [5, 5],
                  ].map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                site.score
              )}
            </div>
          )}
          <div>
            <Link to={`/site/${site.id}/pvreport`}>PV Report</Link>
          </div>
          <div style={{ marginTop: "8px" }}>
            Address:{" "}
            <a
              href={`https://maps.google.com/?q=${site.latitude},${site.longitude}`}
              rel="noreferrer"
              target="_blank"
            >
              {site.address}
            </a>
          </div>
          <div style={{ marginTop: "8px" }}>
            Created:{" "}
            {testMode
              ? "fixed date for test"
              : formatDateTimeFromSeconds(site.createdAt)}
          </div>
          <div>
            Modified:{" "}
            {testMode
              ? "fixed date for test"
              : formatDateTimeFromSeconds(site.updatedAt)}
          </div>
          {!inlineEditMode &&
            (organisation || isFullSiteList) &&
            site.nearbySiteIDs.length > 0 && (
              <div style={{ marginTop: "8px" }}>
                <Link to={`/site/${site.id}/scenarios`}>
                  {site.nearbySiteIDs.length} scenarios
                </Link>
              </div>
            )}
          {inlineEditMode && isCalculating && (
            <div style={{ marginTop: "8px" }}>
              <strong>(Calculating...)</strong>
            </div>
          )}
          {inlineEditMode &&
            !isCalculating &&
            !!inlineEdits &&
            Object.keys(errors).length === 0 && (
              <div style={{ marginTop: "8px" }}>
                <strong>(Unsaved)</strong>
              </div>
            )}
          {inlineEditMode &&
            !isCalculating &&
            !!inlineEdits &&
            Object.keys(errors).length > 0 && (
              <div style={{ marginTop: "8px", color: "red" }}>
                <strong>(Validation failed)</strong>
              </div>
            )}
        </div>
      </td>
      <td width="20%" style={{ fontSize: 13 }}>
        <div>
          PV:{" "}
          <strong>
            {inlineEditMode && (
              <>
                {Object.values(site.generation)
                  .filter((gen) => !!gen)
                  .map((gen, genIdx) => (
                    <Form.Control
                      key={genIdx}
                      className="form-control-ghost-inline"
                      style={{ width: "70px" }}
                      type="text"
                      value={gen.installedCapacity}
                      onChange={(e) =>
                        setInlineEdits(site.id, {
                          site: {
                            generation: {
                              [gen.idx]: { installedCapacity: e.target.value },
                            },
                          },
                        })
                      }
                      data-testid={`inline-edit-installed-capacity-${index}`}
                    />
                  ))}
              </>
            )}
            {!inlineEditMode && (
              <span>
                {formatUnits(
                  parseAsNumber(site.totalInstalledCapacity),
                  "kWp",
                  3
                )}
              </span>
            )}{" "}
            (
            <span
              style={{
                color: siteCalculations.totalRoofUtil >= 1 ? "red" : "inherit",
              }}
            >
              {formatPercentage(siteCalculations.totalRoofUtil, 2)}
            </span>
            , {site.pvForecastProbability})
          </strong>
        </div>
        {inlineEditMode && errors["installedCapacity"] && (
          <div style={{ color: "red" }}>{errors["installedCapacity"]}</div>
        )}
        <div>
          Tenant energy share:{" "}
          <strong>
            {formatPercentage(
              siteCalculations.energyFlowAnnual!.tenantEnergyShare,
              2
            )}
          </strong>
        </div>
        <div>
          Export capacity:{" "}
          <strong>
            {formatUnits(siteCalculations.energyFlowAnnual!.exported, "kWh", 2)}
          </strong>
        </div>
        <div>
          Profile:{" "}
          <strong>
            {[
              ...Object.values(site.demand)
                .filter((d) => !!d)
                .map((d) => d.buildingProfile)
                .map(
                  (profile) =>
                    siteValuesOptions?.buildingProfiles?.[profile]?.name ||
                    "not selected"
                )
                .reduce((acc, curr) => acc.add(curr), new Set()),
            ].join(", ")}{" "}
            ({formatNumber(demandCoefficient, 2, 8)})
          </strong>
        </div>
        <div>
          HH data:{" "}
          <strong>
            {[
              ...Object.values(site.demand)
                .filter((d) => !!d)
                .map((d) => d.halfHourlyDemand?.source)
                .map((demandSource) =>
                  demandSource === "hh_data"
                    ? "Actual"
                    : demandSource === "generated"
                      ? "Generated"
                      : "Inferred"
                )
                .reduce((acc, curr) => acc.add(curr), new Set()),
            ].join(", ")}{" "}
          </strong>
        </div>
        {parseAsNumber(site.totalInstalledCapacity) > 0 &&
          site.substationData?.success && (
            <div>
              Generation headroom:{" "}
              <strong>
                {site.substationData.headroom === undefined
                  ? "Headroom not defined"
                  : `${formatNumber(Math.round((siteRaw.substationData?.headroom ?? 0) * 1000), 0, 0)} kVA`}
              </strong>
            </div>
          )}
        {parseAsNumber(site.totalInstalledCapacity) > 0 && (
          <div>
            Export limit:{" "}
            <strong>
              {site.totalExportLimit !== undefined &&
              site.totalExportLimit !== null
                ? site.totalExportLimit
                : "-"}
            </strong>
          </div>
        )}
        {curtailment !== undefined && (
          <div>
            Curtailment:{" "}
            <strong style={{ color: curtailmentColor }}>{curtailment}</strong>
          </div>
        )}
        {curtailment === undefined &&
          parseAsNumber(site.totalInstalledCapacity) > 0 && (
            <div>
              Curtailment risk:{" "}
              <strong style={{ color: curtailmentColor }}>
                {curtailmentRisk}
              </strong>
            </div>
          )}
        <div>
          {showCustomerIrr && (
            <>
              IRR:{" "}
              <strong
                style={{
                  color:
                    organisation &&
                    !validIRR(siteRaw, siteCalculations, organisation)
                      ? "red"
                      : "inherit",
                }}
              >
                {siteIRR(siteRaw, siteCalculations, organisation)}
              </strong>{" "}
              (License)
            </>
          )}
          {showInrangeIrr && (
            <>
              IRR:{" "}
              <strong>
                {siteIRR(siteRaw, siteCalculations, organisation)}
              </strong>{" "}
              (Lease)
            </>
          )}
        </div>
        <div>
          {parseAsNumber(site.totalInstalledCapacity) === 0 ? (
            <>
              Network import tariff:{" "}
              <strong
                style={{
                  color:
                    organisation &&
                    !validNetworkImportTariff(siteRaw, organisation)
                      ? "red"
                      : "inherit",
                }}
              >
                {formatUnits(
                  siteRaw.tariffConfig.networkImportTariff * 100,
                  fractionalCurrencySymbol(site.currencyCode),
                  2
                )}
              </strong>
            </>
          ) : (
            <>
              {getPpas(site.tariffConfig.onsitePPA, "other").map(
                (tariff, ppaIndex) => (
                  <div key={ppaIndex}>
                    <div>
                      {ppaIndex === 0 && <>Tenant tariff: </>}
                      <strong
                        style={{
                          color:
                            organisation &&
                            !validTenantTariff(siteRaw, organisation, tariff)
                              ? "red"
                              : "inherit",
                        }}
                      >
                        {inlineEditMode && (
                          <Form.Control
                            className="form-control-ghost-inline"
                            style={{ width: "50px" }}
                            type="text"
                            value={tariff.tariff}
                            onChange={(e) =>
                              setInlineEdits(site.id, {
                                site: {
                                  tariffConfig: {
                                    onsitePPA: ppaUpdate(ppaIndex, {
                                      tariff: e.target.value,
                                    }),
                                  },
                                },
                              })
                            }
                            data-testid={`inline-edit-tenant-tariff-${index}-${ppaIndex}-tariff`}
                          />
                        )}
                        {!inlineEditMode && (
                          <span>
                            {formatTariff(
                              parseAsNumber(tariff.tariff),
                              site.tariffConfig.onsitePPA.ppaPurchaseModel!,
                              tariff.ppaLeaseBillingFrequency!,
                              true,
                              site.currencyCode,
                              false,
                              false
                            )}
                          </span>
                        )}{" "}
                        (
                        {inlineEditMode && (
                          <Form.Control
                            className="form-control-ghost-inline"
                            style={{ width: "25px" }}
                            type="text"
                            value={tariff.ppaLength}
                            onChange={(e) =>
                              setInlineEdits(site.id, {
                                site: {
                                  tariffConfig: {
                                    onsitePPA: ppaUpdate(ppaIndex, {
                                      ppaLength: e.target.value,
                                    }),
                                  },
                                },
                              })
                            }
                            data-testid={`inline-edit-tenant-tariff-${index}-${ppaIndex}-ppaLength`}
                          />
                        )}
                        {!inlineEditMode && (
                          <span>{parseAsNumber(tariff.ppaLength)}</span>
                        )}{" "}
                        yrs)
                        {tariff.ppaType === "indexed" && tariff.ppaIndex && (
                          <>
                            @{" "}
                            {inlineEditMode && (
                              <Form.Control
                                className="form-control-ghost-inline"
                                style={{ width: "50px" }}
                                type="text"
                                value={tariff.ppaIndex}
                                onChange={(e) =>
                                  setInlineEdits(site.id, {
                                    site: {
                                      tariffConfig: {
                                        onsitePPA: ppaUpdate(ppaIndex, {
                                          ppaIndex: e.target.value,
                                        }),
                                      },
                                    },
                                  })
                                }
                                data-testid={`inline-edit-tenant-tariff-${index}-${ppaIndex}-ppaIndex`}
                              />
                            )}
                            {!inlineEditMode && (
                              <span>
                                {formatNumber(
                                  parseAsNumber(tariff.ppaIndex) * 100,
                                  1,
                                  6
                                )}
                              </span>
                            )}
                            %
                          </>
                        )}
                      </strong>
                    </div>
                    {inlineEditMode &&
                      (errors[
                        `tariffConfig.onsitePPA${ppaErrorString(ppaIndex)}.tariff`
                      ] ||
                        errors[
                          `tariffConfig.onsitePPA${ppaErrorString(ppaIndex)}.ppaLength`
                        ] ||
                        errors[
                          `tariffConfig.onsitePPA${ppaErrorString(ppaIndex)}.ppaIndex`
                        ]) && (
                        <div style={{ color: "red" }}>
                          {errors[
                            `tariffConfig.onsitePPA${ppaErrorString(ppaIndex)}.tariff`
                          ] ||
                            errors[
                              `tariffConfig.onsitePPA${ppaErrorString(ppaIndex)}.ppaLength`
                            ] ||
                            errors[
                              `tariffConfig.onsitePPA${ppaErrorString(ppaIndex)}.ppaIndex`
                            ]}
                        </div>
                      )}
                  </div>
                )
              )}
            </>
          )}
        </div>
        <div>
          {parseAsNumber(site.totalInstalledCapacity) > 0 && (
            <>
              {getPpas(site.tariffConfig.spillExportPPA, "other").map(
                (tariff, ppaIndex) => (
                  <div key={ppaIndex}>
                    <div>
                      {ppaIndex === 0 && <>Spill export tariff: </>}
                      <strong>
                        {inlineEditMode && (
                          <Form.Control
                            className="form-control-ghost-inline"
                            style={{ width: "50px" }}
                            type="text"
                            value={tariff.tariff}
                            onChange={(e) =>
                              setInlineEdits(site.id, {
                                site: {
                                  tariffConfig: {
                                    spillExportPPA: ppaUpdate(ppaIndex, {
                                      tariff: e.target.value,
                                    }),
                                  },
                                },
                              })
                            }
                            data-testid={`inline-edit-spill-export-tariff-${index}-${ppaIndex}-tariff`}
                          />
                        )}
                        {!inlineEditMode && (
                          <span>
                            {formatTariff(
                              parseAsNumber(tariff.tariff),
                              site.tariffConfig.spillExportPPA
                                .ppaPurchaseModel!,
                              tariff.ppaLeaseBillingFrequency!,
                              true,
                              site.currencyCode,
                              false,
                              false
                            )}
                          </span>
                        )}{" "}
                        (
                        {inlineEditMode && (
                          <Form.Control
                            className="form-control-ghost-inline"
                            style={{ width: "25px" }}
                            type="text"
                            value={tariff.ppaLength}
                            onChange={(e) =>
                              setInlineEdits(site.id, {
                                site: {
                                  tariffConfig: {
                                    spillExportPPA: ppaUpdate(ppaIndex, {
                                      ppaLength: e.target.value,
                                    }),
                                  },
                                },
                              })
                            }
                            data-testid={`inline-edit-spill-export-tariff-${index}-${ppaIndex}-ppaLength`}
                          />
                        )}
                        {!inlineEditMode && (
                          <span>{parseAsNumber(tariff.ppaLength)}</span>
                        )}{" "}
                        yrs)
                        {tariff.ppaType === "indexed" && tariff.ppaIndex && (
                          <>
                            @{" "}
                            {inlineEditMode && (
                              <Form.Control
                                className="form-control-ghost-inline"
                                style={{ width: "50px" }}
                                type="text"
                                value={tariff.ppaIndex}
                                onChange={(e) =>
                                  setInlineEdits(site.id, {
                                    site: {
                                      tariffConfig: {
                                        spillExportPPA: ppaUpdate(ppaIndex, {
                                          ppaIndex: e.target.value,
                                        }),
                                      },
                                    },
                                  })
                                }
                                data-testid={`inline-edit-spill-export-tariff-${index}-${ppaIndex}-ppaIndex`}
                              />
                            )}
                            {!inlineEditMode && (
                              <span>
                                {formatNumber(
                                  parseAsNumber(tariff.ppaIndex) * 100,
                                  1,
                                  6
                                )}
                              </span>
                            )}
                            %
                          </>
                        )}
                      </strong>
                    </div>
                    {inlineEditMode &&
                      (errors[
                        `tariffConfig.spillExportPPA${ppaErrorString(ppaIndex)}.tariff`
                      ] ||
                        errors[
                          `tariffConfig.spillExportPPA${ppaErrorString(ppaIndex)}.ppaLength`
                        ] ||
                        errors[
                          `tariffConfig.spillExportPPA${ppaErrorString(ppaIndex)}.ppaIndex`
                        ]) && (
                        <div style={{ color: "red" }}>
                          {errors[
                            `tariffConfig.spillExportPPA${ppaErrorString(ppaIndex)}.tariff`
                          ] ||
                            errors[
                              `tariffConfig.spillExportPPA${ppaErrorString(ppaIndex)}.ppaLength`
                            ] ||
                            errors[
                              `tariffConfig.spillExportPPA${ppaErrorString(ppaIndex)}.ppaIndex`
                            ]}
                        </div>
                      )}
                  </div>
                )
              )}
            </>
          )}
        </div>
        <div>
          PV cost per kWp:{" "}
          <strong>
            {inlineEditMode && (
              <>
                <div>
                  Install:{" "}
                  <Form.Control
                    className="form-control-ghost-inline"
                    style={{ width: "50px" }}
                    type="text"
                    value={site.costInputsPv.installationCostPerKWp}
                    onChange={(e) =>
                      setInlineEdits(site.id, {
                        site: {
                          costInputsPv: {
                            installationCostPerKWp: e.target.value,
                          },
                        },
                      })
                    }
                    data-testid={`inline-edit-installation-cost-${index}`}
                  />
                </div>
                {errors["costInputsPv.installationCostPerKWp"] && (
                  <div style={{ color: "red" }}>
                    {errors["costInputsPv.installationCostPerKWp"]}
                  </div>
                )}
                <div>
                  Hardware:{" "}
                  <Form.Control
                    className="form-control-ghost-inline"
                    style={{ width: "50px" }}
                    type="text"
                    value={site.costInputsPv.hardwareCostPerKWp}
                    onChange={(e) =>
                      setInlineEdits(site.id, {
                        site: {
                          costInputsPv: {
                            hardwareCostPerKWp: e.target.value,
                          },
                        },
                      })
                    }
                    data-testid={`inline-edit-hardware-cost-${index}`}
                  />
                </div>
                {errors["costInputsPv.hardwareCostPerKWp"] && (
                  <div style={{ color: "red" }}>
                    {errors["costInputsPv.hardwareCostPerKWp"]}
                  </div>
                )}
              </>
            )}
            {!inlineEditMode && (
              <span>
                {formatCurrency(
                  siteRaw.projectCosts.initialCostPerKWp,
                  site.currencyCode
                )}
              </span>
            )}
          </strong>
        </div>
        <div>
          Total cost per kWp:{" "}
          <strong>
            {formatCurrency(
              siteCalculations.projectCosts!.totalCostPerKwp,
              site.currencyCode
            )}
          </strong>
        </div>
        <div>
          Enabling works cost:{" "}
          <strong>
            {inlineEditMode && (
              <>
                <Form.Control
                  className="form-control-ghost-inline"
                  style={{ width: "50px" }}
                  type="text"
                  value={site.costInputsPv.enablingWorksCosts}
                  onChange={(e) =>
                    setInlineEdits(site.id, {
                      site: {
                        costInputsPv: {
                          enablingWorksCosts: e.target.value,
                        },
                      },
                    })
                  }
                  data-testid={`inline-edit-enabling-works-cost-${index}`}
                />
                {errors["costInputsPv.enablingWorksCosts"] && (
                  <div style={{ color: "red" }}>
                    {errors["costInputsPv.enablingWorksCosts"]}
                  </div>
                )}
              </>
            )}
            {!inlineEditMode && (
              <span>
                {formatCurrency(
                  siteRaw.costInputsPv.enablingWorksCosts,
                  site.currencyCode
                )}
              </span>
            )}
          </strong>
        </div>
        {siteCalculations.financialModelsInrange?.installArbitrage !==
          undefined &&
          siteCalculations.financialModelsInrange.installArbitrage !== 0 && (
            <div>
              Arbitrage:{" "}
              <strong>
                {formatCurrency(
                  siteCalculations.financialModelsInrange.installArbitrage,
                  site.currencyCode
                )}
              </strong>
            </div>
          )}
      </td>
      <td width="20%" className="secondary" style={{ fontSize: 14 }}>
        {[...site.siteOwnerships]
          .sort(sortBy((siteOrg) => `${siteOrg.ownership}:${siteOrg.name}`))
          .map((siteOrg) => (
            <div key={siteOrg.orgID} style={{ fontSize: 13 }}>
              {SITE_ORG_LABELS[siteOrg.ownership]}:{" "}
              <strong>
                <Link to={`/organisation/${siteOrg.orgID}/edit`}>
                  {testMode
                    ? siteOrg.name!.replace(/-.*/, " - fixed-id-for-testing")
                    : siteOrg.name}
                </Link>
              </strong>
            </div>
          ))}
        <div style={{ fontSize: 13 }}>
          Lease length: <strong>{site.leaseLengthDate || "-"}</strong>
        </div>
        <div style={{ fontSize: 13 }}>
          Operational status:{" "}
          <strong>{STATUS_OPTIONS[site.operationalStatus]}</strong>
        </div>
        {site.commerciallyOperationalDate && (
          <div style={{ fontSize: 13 }}>
            CO date: <strong>{site.commerciallyOperationalDate}</strong>
          </div>
        )}
        <div style={{ fontSize: 13 }}>
          PV design selected:{" "}
          <strong>{site.pvDesignSystemLastUpdatedAt ? "True" : "False"}</strong>
        </div>
        <div style={{ fontSize: 13 }}>
          PV hardware:{" "}
          <strong>
            {site.pvPanelName} / {site.pvInverterBrand}
          </strong>
        </div>
        {siteCalculations.totalInverters.length > 0 && (
          <>
            <div style={{ fontSize: 13 }}>
              PV inverters:{" "}
              <strong>
                {siteCalculations.totalInverters
                  .sort(sortBy((a) => a.count))
                  .map((inverter) => `${inverter.name} x ${inverter.count}`)
                  .join(", ")}
              </strong>
            </div>
            <div style={{ fontSize: 13 }}>
              Pnom ratio:{" "}
              <strong>
                {formatNumber(
                  parseAsNumber(site.totalInstalledCapacity) /
                    inverterRatedOutputPowerTotalKwpAc(
                      siteValuesOptions!.pvInverters,
                      siteCalculations.totalInverters
                    ),
                  0,
                  2,
                  false
                )}
              </strong>
            </div>
          </>
        )}
        <div style={{ fontSize: 13 }}>
          Battery:{" "}
          <strong>
            {siteRaw.totalBatterySummary && (
              <>
                <span>{formatBatterySummary(siteRaw.totalBatterySummary)}</span>
                {siteRaw.totalBatterySummary.batteryInstalls.map((b, i) => (
                  <div key={i}>
                    {formatBatteryModel(b)} (HW:{" "}
                    {formatCurrency(
                      siteRaw.costInputsBattery.costs[b.modelName].hardwareCost,
                      site.currencyCode
                    )}
                    , Install:{" "}
                    {formatCurrency(
                      siteRaw.costInputsBattery.costs[b.modelName]
                        .installationCost,
                      site.currencyCode
                    )}{" "}
                    x {b.count})
                  </div>
                ))}
              </>
            )}
            {!siteRaw.totalBatterySummary && "None"}
          </strong>
        </div>
        <div style={{ fontSize: 13 }}>
          SDM:{" "}
          <strong>
            {
              site.sdmMatches.filter((match) => match.sellerId === site.id)
                .length
            }{" "}
            sell /{" "}
            {
              site.sdmMatches.filter((match) => match.buyerId === site.id)
                .length
            }{" "}
            buy
          </strong>
        </div>
      </td>
      <td
        style={{
          fontSize: 13,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "normal",
        }}
        align="left"
      >
        {inlineEditMode && (
          <Form.Control
            className="form-control-ghost"
            as="textarea"
            rows={15}
            cols={120}
            value={site.notes}
            style={{ fontSize: 13 }}
            onChange={(e) =>
              setInlineEdits(site.id, {
                site: {
                  notes: e.target.value,
                },
              })
            }
            data-testid={`inline-edit-notes-${index}`}
          />
        )}
        {!inlineEditMode && site.locked && (
          <>
            <div>Locked: {stringToReactNodesWithLinks(site.lockNote)}</div>
            <hr />
          </>
        )}
        {!inlineEditMode && <>{stringToReactNodesWithLinks(site.notes)}</>}
      </td>
    </tr>
  );
};

export default MemoizedSitesTableRow;
